/**
 * OpenStreetMap — Overpass API client for in-app "auto services" categories
 * that aren't covered by data.gov.il (parking, car-wash, car-parts).
 *
 * Overpass returns OSM nodes/ways/relations matching a tag query around a
 * geographic point. We use `out center;` so multi-node `way` features (e.g.
 * a parking lot polygon) come back with a single representative coordinate.
 *
 * @typedef {'parking' | 'car_wash' | 'car_parts'} OsmKind
 *
 * @typedef {Object} OsmPlace
 * @property {string}  id        stable id (`${osmType}/${osmId}`)
 * @property {OsmKind} kind
 * @property {string}  name      display name (may fall back to category label)
 * @property {number}  lat
 * @property {number}  lon
 * @property {string}  [brand]   chain/operator (e.g. "מילגם", "אחים שאקי")
 * @property {string}  [address] street + housenumber if tagged
 * @property {string}  [city]
 * @property {string}  [phone]
 * @property {string}  [openingHours]
 * @property {string}  [extra]   ad-hoc supplemental fact (capacity / fee / type)
 */

import { Overpass } from '../../app/config.js';
import { createLogger } from '../../core/logger.js';
import { reverseGeocode } from './locationiq.js';

const log = createLogger('osm');

/** Map our app-level category to the OSM tag selector(s) used in Overpass QL.
 *  Multiple selectors per kind are unioned in the query — for car_parts we
 *  also include tyres shops, motorcycle repair (often sells parts), and the
 *  `craft=car_parts` tag (used by smaller workshops). */
const OSM_SELECTORS = /** @type {Record<OsmKind, string[]>} */ ({
  parking: [
    'node["amenity"="parking"]',
    'way["amenity"="parking"]',
  ],
  car_wash: [
    'node["amenity"="car_wash"]',
    'way["amenity"="car_wash"]',
  ],
  car_parts: [
    'node["shop"="car_parts"]',
    'way["shop"="car_parts"]',
    'node["shop"="tyres"]',
    'way["shop"="tyres"]',
    'node["shop"="motorcycle_repair"]',
    'way["shop"="motorcycle_repair"]',
    'node["craft"="car_parts"]',
  ],
});

const FALLBACK_NAMES = /** @type {Record<OsmKind, string>} */ ({
  parking: 'חניון',
  car_wash: 'רחיצת רכב',
  car_parts: 'חנות חלפים',
});

/**
 * Fetch POIs of a given kind around `(lat, lon)` within `radiusM` metres.
 * Errors return an empty array (caller decides whether to retry).
 *
 * @param {Object} args
 * @param {OsmKind} args.kind
 * @param {number}  args.lat
 * @param {number}  args.lon
 * @param {number}  [args.radiusM=Overpass.defaultRadiusM]
 * @returns {Promise<OsmPlace[]>}
 */
export async function fetchNearby({ kind, lat, lon, radiusM = Overpass.defaultRadiusM }) {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return [];
  const selectors = OSM_SELECTORS[kind];
  if (!selectors) { log.warn('unknown kind:', kind); return []; }

  const query = buildQuery(selectors, radiusM, lat, lon);

  // Race ALL endpoints in parallel. Crucially, EMPTY-but-200 responses are
  // treated as rejection inside the race so `Promise.any` keeps waiting for
  // a mirror that actually has data — otherwise the first mirror to come
  // back with `{ elements: [] }` (which happens when a mirror is partially
  // sync'd or rate-limits to nothing) wins and discards the slower mirrors
  // that would have returned the real list.
  const racing = Overpass.endpoints.map((endpoint) =>
    postOverpass(endpoint, query, Overpass.timeoutMs).then((j) => {
      if (!(j?.elements?.length > 0)) throw new Error('empty');
      return j;
    }),
  );

  /** @type {any} */
  let data;
  try {
    data = await Promise.any(racing);
  } catch {
    // Every endpoint either failed or returned an empty set. Caller treats
    // [] as "no results" — true whether the area genuinely has none or
    // every mirror was unreachable.
    return [];
  }

  const normalized = normalize(kind, data?.elements ?? []);
  return await enrichWithAddresses(normalized);
}

/**
 * For POIs whose `name` was a generic fallback (i.e. no name/operator/brand
 * tag in OSM) AND that have no address, ask LocationIQ what's at that
 * coordinate. Runs in parallel with a hard cap so a huge result list doesn't
 * eat the API quota.
 *
 * @param {OsmPlace[]} places
 * @returns {Promise<OsmPlace[]>}
 */
async function enrichWithAddresses(places) {
  const GENERIC_NAMES = new Set(Object.values(FALLBACK_NAMES));
  const MAX_LOOKUPS = 12;

  const needsLookup = (/** @type {OsmPlace} */ p) =>
    !p.address && GENERIC_NAMES.has(p.name);

  /** @type {Array<{ idx: number; promise: Promise<any> }>} */
  const lookups = [];
  for (let i = 0; i < places.length && lookups.length < MAX_LOOKUPS; i++) {
    if (needsLookup(places[i])) {
      lookups.push({ idx: i, promise: reverseGeocode(places[i].lat, places[i].lon) });
    }
  }
  if (lookups.length === 0) return places;

  const results = await Promise.allSettled(lookups.map((l) => l.promise));
  /** @type {OsmPlace[]} */
  const enriched = places.slice();
  results.forEach((r, j) => {
    if (r.status !== 'fulfilled') return;
    const { idx } = lookups[j];
    const short = r.value?.shortAddress;
    const city = r.value?.city;
    if (short) {
      enriched[idx] = { ...enriched[idx], address: short };
    } else if (city && !enriched[idx].city) {
      enriched[idx] = { ...enriched[idx], city };
    }
  });
  return enriched;
}

// ====================================================================
// internals
// ====================================================================

/**
 * @param {string[]} selectors
 * @param {number}   radiusM
 * @param {number}   lat
 * @param {number}   lon
 */
function buildQuery(selectors, radiusM, lat, lon) {
  const around = `(around:${radiusM},${lat},${lon})`;
  const stmts = selectors.map((s) => `${s}${around};`).join('\n  ');
  return `[out:json][timeout:${Math.floor(Overpass.timeoutMs / 1000)}];\n(\n  ${stmts}\n);\nout center 60;`;
}

/**
 * @param {string} endpoint
 * @param {string} body
 * @param {number} timeoutMs
 */
async function postOverpass(endpoint, body, timeoutMs) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      body: 'data=' + encodeURIComponent(body),
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      signal: ctrl.signal,
      cache: 'no-store',
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

/**
 * @param {OsmKind} kind
 * @param {any[]} elements
 * @returns {OsmPlace[]}
 */
function normalize(kind, elements) {
  /** @type {OsmPlace[]} */
  const out = [];
  for (const el of elements) {
    const tags = el?.tags ?? {};
    const lat = el.lat ?? el.center?.lat;
    const lon = el.lon ?? el.center?.lon;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;

    // Display name — OSM in Israel is uneven, so try several tags before
    // falling back to a generic kind label. `operator` and `brand` are
    // commonly tagged on unnamed POIs (chains: מילגם, אחים שאקי, פז גז).
    const nameHe = tags['name:he'] || tags['name:iw'];
    const nameEn = tags['name:en'];
    const fallbackLabel = FALLBACK_NAMES[kind];
    const operator = tags.operator || tags['operator:he'] || undefined;
    const brand = tags.brand || tags['brand:he'] || undefined;
    let name = nameHe || tags.name || nameEn || operator || brand;
    if (!name) {
      // Last resort: if we have a street we can at least say "חניון ב<רחוב>"
      const street = tags['addr:street'];
      name = street ? `${fallbackLabel} ב${street}` : fallbackLabel;
    }

    out.push({
      id: `${el.type}/${el.id}`,
      kind,
      name,
      lat,
      lon,
      // Keep operator/brand visible separately when they aren't already the
      // name (so e.g. "מילגם — חניון רוטשילד 22" reads naturally).
      brand: (operator && operator !== name ? operator : (brand && brand !== name ? brand : undefined)),
      address: composeAddress(tags),
      city: tags['addr:city'] || undefined,
      phone: tags.phone || tags['contact:phone'] || undefined,
      openingHours: tags.opening_hours || undefined,
      extra: composeExtra(kind, tags),
    });
  }
  return out;
}

/** @param {Record<string, string>} t */
function composeAddress(t) {
  const street = t['addr:street'];
  const num = t['addr:housenumber'];
  if (street && num) return `${street} ${num}`;
  return street || undefined;
}

/**
 * Build a one-liner of extra facts the user might care about per kind:
 *   - parking: type + capacity + fee
 *   - car_wash: self-service marker
 *   - car_parts: nothing extra (the shop's own name is enough)
 *
 * @param {OsmKind} kind
 * @param {Record<string, string>} t
 */
function composeExtra(kind, t) {
  /** @type {string[]} */
  const parts = [];
  if (kind === 'parking') {
    const typeLabel = parkingTypeLabel(t.parking);
    if (typeLabel) parts.push(typeLabel);
    if (t.capacity) parts.push(`${t.capacity} מקומות`);
    if (t.fee === 'yes' || t.fee === 'true') parts.push('בתשלום');
    else if (t.fee === 'no' || t.fee === 'false') parts.push('חינם');
  } else if (kind === 'car_wash') {
    if (t.self_service === 'yes') parts.push('שירות עצמי');
    else if (t.self_service === 'no') parts.push('שטיפה מלאה');
  }
  return parts.length ? parts.join(' · ') : undefined;
}

/** @param {string | undefined} v */
function parkingTypeLabel(v) {
  switch (v) {
    case 'underground':   return 'תת-קרקעי';
    case 'multi-storey':  return 'רב-קומתי';
    case 'surface':       return 'משטח חניה';
    case 'street_side':   return 'בצד הכביש';
    case 'rooftop':       return 'על הגג';
    default:              return undefined;
  }
}
